import React, { useState, useEffect } from 'react'
import { gql, useQuery } from '@apollo/client'
import Dialog from 'components/dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import TextField from '@material-ui/core/TextField'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Divider from '@material-ui/core/Divider'

import { useDebounce, useIsMobile } from 'lib/hooks'
import { useAuth } from 'lib/auth'
import { FAMILY_GOLF_TEMPLATE_SLUG } from 'pages/pga-coach/bookings/events/create-event-form/can-submit-create-event'
import { makeStyles } from '@material-ui/core/styles'
import { PROMO_CALLOUT_BORDER_RADIUS } from 'lib/form-field-styles'
import GroupEventTemplateCard from './group-event-template-card'

const GET_EVENT_BY_SLUG = gql`
  query GroupEventBySlug(
    $slug: String!
    $tags: [String!]
    $templateSlugs: [String!]
  ) {
    groupEvents(slug: $slug, tags: $tags, templateSlugs: $templateSlugs) {
      slug
      academyRequired
      title
      tags
      description
      startDateTime
      endDateTime
      coachFacility {
        name
      }
      featuredImageUrl
      priceInCents
      template {
        id
        slug
      }
    }
  }
`

const useStyles = makeStyles(theme => ({
  paper: {
    borderRadius: PROMO_CALLOUT_BORDER_RADIUS,
  },
  dialogTitle: {
    padding: theme.spacing(4),
    paddingRight: theme.spacing(7),
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  dialogContent: {
    padding: theme.spacing(4),
  },
  sectionHeading: {
    fontSize: '1rem',
    fontWeight: 400,
    color: theme.palette.text.primary,
    lineHeight: 1.3,
    marginBottom: theme.spacing(0.5),
  },
  sectionHelper: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    lineHeight: 1.5,
  },
  linkExamplesHeading: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  linkExample: {
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
}))

const getFreeTierCopyFilters = (isFreeTierReached, isPgaHopeCoach) => {
  if (!isFreeTierReached) return { tags: [], templateSlugs: [] }
  return {
    tags: isPgaHopeCoach ? ['PGA HOPE'] : [],
    templateSlugs: ['family-golf'],
  }
}

const isCopyableAtLimit = (event, isPgaHopeCoach) =>
  event?.template?.slug === FAMILY_GOLF_TEMPLATE_SLUG ||
  (isPgaHopeCoach && Boolean(event?.tags?.includes('PGA HOPE')))

const CopyFromLink = ({
  onCreateClick,
  isFreeTierReached,
  isPgaHopeCoach,
  classes,
}) => {
  const [link, setLink] = useState('')
  const debouncedLink = useDebounce(link, 500)
  const [slug, setSlug] = useState(null)

  useEffect(() => {
    const regex = /((https?:\/\/)?(((www|my)\.)?((dev|qa)\.)?pga\.com)\/things-to-do\/events\/)?(?<slug>(\w|-)+)(\/\w+)?/
    const parsedSlug = debouncedLink?.match(regex)?.groups?.slug
    setSlug(parsedSlug)
  }, [debouncedLink, setSlug])
  const { tags, templateSlugs } = getFreeTierCopyFilters(
    isFreeTierReached,
    isPgaHopeCoach,
  )
  const { data, loading } = useQuery(GET_EVENT_BY_SLUG, {
    variables: {
      slug,
      tags,
      templateSlugs,
    },
    skip: !slug,
  })

  // When isFreeTierReached is true, server returns only Family Golf events, plus PGA HOPE events for PGA HOPE coaches
  // When false, we filter out PGA HOPE events from being copied
  const matchedEvent = isFreeTierReached
    ? data?.groupEvents?.filter(event =>
      isCopyableAtLimit(event, isPgaHopeCoach),
    )?.[0]
    : data?.groupEvents?.filter(
      event => !event?.slug?.includes('pga-hope-'),
    )?.[0]

  return (
    <>
      <Box mb={2}>
        <Typography className={classes.sectionHeading}>
          Copy from event link
        </Typography>
        <Typography className={classes.sectionHelper}>
          Enter the link of the event you'd like to copy.
        </Typography>
      </Box>
      <Box mb={2}>
        <TextField
          fullWidth
          variant="outlined"
          label="Event Link"
          value={link}
          onChange={e => setLink(e.target.value)}
        />
      </Box>
      <Box mb={2}>
        <Typography className={classes.linkExamplesHeading}>
          Event links should look like:
        </Typography>
        <Typography display="block" className={classes.linkExample}>
          &bull; https://www.pga.com/things-to-do/events/chip-n-sip
        </Typography>
        <Typography display="block" className={classes.linkExample}>
          &bull; https://my.pga.com/things-to-do/events/chip-n-sip/register
        </Typography>
        <Typography display="block" className={classes.linkExample}>
          &bull; chip-n-sip
        </Typography>
      </Box>
      {loading && (
        <Box textAlign="center">
          <CircularProgress color="primary" />
        </Box>
      )}
      {!loading && (
        <>
          {matchedEvent && (
            <GroupEventTemplateCard
              event={matchedEvent}
              callToAction="Copy Event"
              onCreateClick={onCreateClick}
            />
          )}
          {slug && !matchedEvent && (
            <Typography variant="caption" color="error">
              Event not found. The link provided is invalid or does not match an
              event.
            </Typography>
          )}
        </>
      )}
    </>
  )
}

const RECENT_EVENTS = gql`
  query RecentGroupEvents($tags: [String!], $templateSlugs: [String!]) {
    currentUser {
      groupEvents(
        tags: $tags
        templateSlugs: $templateSlugs
        orderBy: [{ field: CREATED_AT, direction: DESC }]
      ) {
        academyRequired
        id
        title
        tags
        description
        startDateTime
        endDateTime
        slug
        coachFacility {
          id
          name
        }
        featuredImageUrl
        priceInCents
        collectAttendance
        template {
          id
          slug
        }
      }
    }
  }
`

const RecentEventSelection = ({
  onCreateClick,
  isFreeTierReached,
  isPgaHopeCoach,
  classes,
}) => {
  const { tags, templateSlugs } = getFreeTierCopyFilters(
    isFreeTierReached,
    isPgaHopeCoach,
  )
  const { data, loading } = useQuery(RECENT_EVENTS, {
    variables: {
      tags,
      templateSlugs,
    },
  })
  // When isFreeTierReached is true, server returns only Family Golf events, plus PGA HOPE events for PGA HOPE coaches
  // When false, we filter out PGA HOPE events from being copied
  const events = isFreeTierReached
    ? (data?.currentUser?.groupEvents || []).filter(event =>
      isCopyableAtLimit(event, isPgaHopeCoach),
    )
    : data?.currentUser?.groupEvents?.filter(
      event => !event.slug?.includes('pga-hope'),
    ) || []

  return (
    <>
      <Box mb={2}>
        <Typography className={classes.sectionHeading}>
          Copy recent event
        </Typography>
        <Typography className={classes.sectionHelper}>
          Choose an event you've recently created.
        </Typography>
      </Box>
      {loading && (
        <Box textAlign="center">
          <CircularProgress color="primary" />
        </Box>
      )}
      {!loading && (
        <Grid container spacing={4}>
          {events.map(event => (
            <Grid key={event.id} item lg={6} sm={12}>
              <GroupEventTemplateCard
                event={event}
                callToAction="Copy Event"
                onCreateClick={onCreateClick}
              />
            </Grid>
          ))}
          {events.length === 0 && (
            <Grid item>
              <Typography variant="caption">
                No events found. Events you create will appear here.
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </>
  )
}

const CopyExistingEventDialog = ({
  trigger,
  isOpen,
  onClose,
  isFreeTierReached,
  onAfterSelect,
}) => {
  const classes = useStyles()
  const isMobile = useIsMobile()
  const { user } = useAuth()
  const isPgaHopeCoach = !!user?.coach?.pgaHopeCoach
  return (
    <Dialog
      trigger={trigger}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      showCloseButton
      classes={{ paper: classes.paper }}
    >
      {({ closeDialog }) => {
        return (
          <>
            <DialogTitle className={classes.dialogTitle} disableTypography>
              <Typography className={classes.title} component="h1">
                Copy Existing Event
              </Typography>
              <Typography className={classes.subtitle}>
                Paste an event link or choose one of your recent events to
                duplicate.
              </Typography>
            </DialogTitle>
            <DialogContent dividers className={classes.dialogContent}>
              <Grid container spacing={4}>
                <Grid item md={4} sm={12}>
                  <CopyFromLink
                    classes={classes}
                    onCreateClick={() => {
                      closeDialog()
                      onAfterSelect?.()
                    }}
                    isFreeTierReached={isFreeTierReached}
                    isPgaHopeCoach={isPgaHopeCoach}
                  />
                </Grid>
                {isMobile && (
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                )}
                <Grid item md={8} sm={12}>
                  <RecentEventSelection
                    classes={classes}
                    onCreateClick={() => {
                      closeDialog()
                      onAfterSelect?.()
                    }}
                    isFreeTierReached={isFreeTierReached}
                    isPgaHopeCoach={isPgaHopeCoach}
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )
      }}
    </Dialog>
  )
}

export default CopyExistingEventDialog
